/**
 * Attendance/attestation panel shown on the Action detail page, visible
 * only to the owning group's group_admin/group_editor. RSVP itself lives
 * in ActionRsvpButton (rendered inline in PostCard, feed + detail alike).
 *
 * "Attest someone else" searches ALL accounts (searchApi.searchAccounts,
 * same endpoint the @mention autocomplete uses), not just this group's
 * members — attestation is meant to cover anyone who showed up, member or
 * not, RSVP'd or not (see spec-action-eot-mvp.md's independent-of-RSVP
 * rule). A plain <select> of group members doesn't cover that case at all.
 */
import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Check, Search, X } from 'lucide-react'
import { SYSTEM_BADGE_SLUGS } from '@olimhub/shared'
import type { ActionPost, AccountWithStats, Account } from '@olimhub/shared'
import { actionsApi, groupsApi, searchApi } from '@/api'
import { useAuthStore } from '@/store/auth.store'
import { useToast } from '@/components/common/Toast'
import { Avatar } from '@/components/profile/Avatar'

const SECTION_STYLE: React.CSSProperties = {
  background: 'var(--surface)',
  borderTop: '1px solid var(--border)',
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}

const PILL_BUTTON: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  fontWeight: 700,
  fontSize: 'var(--fs-sm)',
}

interface ActionManagePanelProps {
  post: ActionPost
}

export function ActionManagePanel({ post }: ActionManagePanelProps) {
  const { t } = useTranslation()
  const { isAuthenticated, account } = useAuthStore()
  const { toast } = useToast()
  const action = post.action

  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<AccountWithStats[]>([])
  const [selectedAccount, setSelectedAccount] = useState<AccountWithStats | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data: activeMembers } = useQuery({
    queryKey: ['group-members', action.group.slug, 'active'],
    queryFn: () => groupsApi.getMembers(action.group.slug, 'active'),
    enabled: isAuthenticated,
  })
  const viewerMember = activeMembers?.find((m) => m.account.id === account?.id)
  const viewerRoleBadge = viewerMember?.account.badges.find(
    (ab) => ab.badge.scope === 'group' && ab.groupId === action.group.id,
  )
  const canManage =
    viewerRoleBadge?.badge.slug === SYSTEM_BADGE_SLUGS.GROUP_ADMIN ||
    viewerRoleBadge?.badge.slug === SYSTEM_BADGE_SLUGS.GROUP_EDITOR

  const { data: participations, refetch: refetchParticipations } = useQuery({
    queryKey: ['action-participations', post.id],
    queryFn: () => actionsApi.getParticipations(post.id),
    enabled: canManage,
  })
  const { data: rsvps } = useQuery({
    queryKey: ['action-rsvps', post.id],
    queryFn: () => actionsApi.getRsvps(post.id),
    enabled: canManage,
  })
  const attestedIds = new Set((participations ?? []).map((p) => p.account.id))

  const attestMutation = useMutation({
    mutationFn: (accountId: string) => actionsApi.attest(post.id, accountId),
    onSuccess: () => {
      void refetchParticipations()
      setSelectedAccount(null)
      setQuery('')
      setSearchResults([])
    },
    onError: () => toast.error('Something went wrong. Please try again.'),
  })
  const unattestMutation = useMutation({
    mutationFn: (accountId: string) => actionsApi.unattest(post.id, accountId),
    onSuccess: () => refetchParticipations(),
    onError: () => toast.error('Something went wrong. Please try again.'),
  })

  // Debounced account search — searches all accounts, not just this
  // group's members (see file header comment).
  useEffect(() => {
    const q = query.trim()
    if (q.length < 3) { setSearchResults([]); return }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      searchApi.searchAccounts(q)
        .then((data) => setSearchResults(data.items.slice(0, 8)))
        .catch(() => setSearchResults([]))
    }, 250)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  if (!canManage) return null

  const visibleResults = searchResults.filter((r) => !attestedIds.has(r.id))

  // The list must show everyone relevant to this Action — RSVP'd people AND
  // people attested without ever RSVPing (e.g. found via the search below,
  // or anyone who just showed up). Showing only RSVPs made attested-only
  // people disappear from the UI after confirming them, even though the
  // attestation was recorded — see spec-action-eot-mvp.md follow-up.
  const participantMap = new Map<string, { account: Account; rsvped: boolean }>()
  for (const r of rsvps ?? []) participantMap.set(r.accountId, { account: r.account, rsvped: true })
  for (const p of participations ?? []) {
    if (!participantMap.has(p.account.id)) participantMap.set(p.account.id, { account: p.account, rsvped: false })
  }
  const participants = [...participantMap.entries()].map(([accountId, v]) => ({ accountId, ...v }))

  return (
    <div style={SECTION_STYLE}>
      <h2 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--text-strong)' }}>
        {t('actions.manage_title')}
      </h2>

      {participants.length === 0 && (
        <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          {t('actions.no_participants')}
        </p>
      )}

      {participants.map((p) => {
        const attested = attestedIds.has(p.accountId)
        return (
          <div key={p.accountId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link to={`/@${p.account.username}`} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, textDecoration: 'none', minWidth: 0 }}>
              <Avatar name={p.account.displayName || p.account.username} avatarUrl={p.account.avatarUrl} animalAvatar={p.account.animalAvatar} size="sm" />
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.account.displayName || p.account.username}
              </span>
              {!p.rsvped && (
                <span style={{ flexShrink: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
                  {t('actions.no_rsvp_tag')}
                </span>
              )}
            </Link>
            <button
              type="button"
              onClick={() => (attested ? unattestMutation.mutate(p.accountId) : attestMutation.mutate(p.accountId))}
              style={{
                ...PILL_BUTTON,
                padding: '6px 14px',
                fontSize: 'var(--fs-xs)',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                flexShrink: 0,
                background: attested ? 'var(--action)' : 'var(--surface-2)',
                color: attested ? '#fff' : 'var(--text-muted)',
              }}
            >
              {attested && <Check size={12} />}
              {attested ? t('actions.attested') : t('actions.attest')}
            </button>
          </div>
        )
      })}

      <div style={{ marginTop: 4 }}>
        <label style={{ display: 'block', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
          {t('actions.attest_other_label')}
        </label>

        {selectedAccount ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <Avatar name={selectedAccount.displayName || selectedAccount.username} avatarUrl={selectedAccount.avatarUrl} animalAvatar={selectedAccount.animalAvatar} size="sm" />
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                {selectedAccount.displayName || selectedAccount.username}
              </span>
              <button
                type="button"
                aria-label="Clear"
                onClick={() => setSelectedAccount(null)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-faint)', display: 'flex', padding: 2 }}
              >
                <X size={14} />
              </button>
            </div>
            <button
              type="button"
              disabled={attestMutation.isPending}
              onClick={() => attestMutation.mutate(selectedAccount.id)}
              style={{ ...PILL_BUTTON, background: 'var(--action)', color: '#fff' }}
            >
              {t('actions.attest')}
            </button>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <Search size={15} style={{ position: 'absolute', insetInlineStart: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)' }} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onBlur={() => setTimeout(() => setSearchResults([]), 150)}
                placeholder={t('actions.attest_other_placeholder')}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 10px 8px 32px',
                  borderRadius: 'var(--radius-md)',
                  border: '1.5px solid var(--border)',
                  background: 'var(--bg-sunk)',
                  color: 'var(--text-strong)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  outline: 'none',
                }}
              />
            </div>
            {visibleResults.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  insetInlineStart: 0,
                  insetInlineEnd: 0,
                  marginTop: 4,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-modal)',
                  zIndex: 'var(--z-sheet)',
                  overflow: 'hidden',
                }}
              >
                {visibleResults.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => { setSelectedAccount(r); setQuery(''); setSearchResults([]) }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      padding: '8px 10px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      textAlign: 'start',
                    }}
                  >
                    <Avatar name={r.displayName || r.username} avatarUrl={r.avatarUrl} animalAvatar={r.animalAvatar} size="sm" />
                    <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                      {r.displayName || r.username}
                    </span>
                    <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
                      @{r.username}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
